/* eslint-disable no-console */

import _ from 'lodash';
import * as utils from '../utils';
import * as Logger from '../commons/logger';
import * as config from '../commons/config';
import * as constants from '../commons/constants';
import * as perf from '../commons/performance-logger';
import * as gridService from '../services/gridService';
import * as branchService from '../services/branchService';
import * as analyticsService from '../services/analyticsService';
import * as testimServicesApi from '../commons/testimServicesApi';
import { v4 as uuidv4 } from 'uuid';
import { Color } from '../utils/color';
import { promiseTimeout } from '../utils';
import { reporter } from '../reports/reporter';
import { TestRunStatus } from '../testRunStatus';
import { StopRunOnError, ArgError } from '../errors';
import { WorkerManager } from './WorkerManager';
import { featureFlagsService } from '../commons/featureFlagsService';
import { getSuite, calcTestResultStatus, validateConfig } from './runnerUtils';
import type { RunnerOptions } from 'src/runOptions';
import type { RunListResult } from 'services/src/suite/service';

const { CLI_MODE } = constants;
const logger = Logger.getLogger('test-plan-runner');

export type ExecutionList = Awaited<ReturnType<typeof getSuite>>['tests'][number];

export class TestPlanRunner {
    startTime = Date.now();
    constructor(private readonly customExtensionLocalLocation: string | undefined) { }

    private static _id: string = uuidv4();

    public static get id() { return TestPlanRunner._id; }

    private async runTestAllPhases(
        beforeTests: ExecutionList,
        tests: ExecutionList,
        afterTests: ExecutionList,
        branchToUse: string,
        tpOptions: RunnerOptions,
        executionId: string,
        executionName: string,
        testStatus: TestRunStatus,
    ) {
        const executionResults: { [testResultId: string]: any } = {};
        const workerManager = new WorkerManager(this.customExtensionLocalLocation);
        const runBeforeTests = async () => {
            const workerCount = tpOptions.beforeParallel || 1;
            const stopOnError = true;
            const beforeTestsResults = await workerManager.runTests(beforeTests, testStatus, executionId, executionName, tpOptions, branchToUse, workerCount, stopOnError);
            Object.assign(executionResults, beforeTestsResults);
        };

        const runTestPlanTests = async () => {
            const workerCount = config.TESTIM_CONCURRENT_WORKER_COUNT || tpOptions.parallel;
            const stopOnError = false;
            perf.log('right before this.workerManager.runTests');
            const testsResults = await workerManager.runTests(tests, testStatus, executionId, executionName, tpOptions, branchToUse, workerCount, stopOnError);
            perf.log('right after this.workerManager.runTests');
            Object.assign(executionResults, testsResults);
        };

        const runAfterTests = async () => {
            const workerCount = tpOptions.afterParallel || 1;
            const stopOnError = false;
            const afterTestsResults = await workerManager.runTests(afterTests, testStatus, executionId, executionName, tpOptions, branchToUse, workerCount, stopOnError);
            Object.assign(executionResults, afterTestsResults);
        };

        analyticsService.analyticsExecsStart({ executionId, projectId: tpOptions.project });
        perf.log('right before runBeforeTests');
        try {
            await runBeforeTests();
            perf.log('right before runTestPlanTests');
            await runTestPlanTests();
            perf.log('right after runTestPlanTests');
            await runAfterTests();
            return executionResults;
        } catch (err) {
            logger.error('error running test plan', { err });
            if (err instanceof StopRunOnError) {
                return testStatus.markAllQueuedTests(executionId, constants.runnerTestStatus.ABORTED, 'aborted', false);
            }
            throw err;
        } finally {
            await handlePixelValidationBatches();
        }

        async function handlePixelValidationBatches() {
            const CLOSE_APPLITOOLS_BATCHES_TIMEOUT = 10000;
            if (tpOptions.lightweightMode?.disablePixelValidation) {
                return;
            }
            // When sessionPlayer is available, use it - as it only attempts to close batches that exist.
            if (tpOptions.mode === CLI_MODE.SELENIUM) {
                try {
                    const [{ getSessionPlayer }, { makeSDK }] = await Promise.all([import('../commons/getSessionPlayerRequire'), import('@applitools/eyes-sdk-core')]);
                    const { EyeSdkBuilder } = getSessionPlayer();
                    await promiseTimeout(EyeSdkBuilder.closeBatch(executionId, makeSDK), CLOSE_APPLITOOLS_BATCHES_TIMEOUT);
                    return;
                } catch {
                    // No need to log
                }
            }
            let applitoolsIntegrationData: Awaited<ReturnType<typeof testimServicesApi['getApplitoolsIntegrationData']>> | undefined;
            try {
                if (!tpOptions.company?.activePlan?.premiumFeatures?.applitools) {
                    return;
                }
                applitoolsIntegrationData = await testimServicesApi.getApplitoolsIntegrationData(tpOptions.project);
                if (_.isEmpty(applitoolsIntegrationData) || !executionId) {
                    return;
                }
                const { runKey: apiKey, url: serverUrl } = applitoolsIntegrationData;
                const tmpSDK = (require('@applitools/eyes-sdk-core') as typeof import('@applitools/eyes-sdk-core')).makeSDK({ name: 'Testim.io', version: '4.0.0', spec: {} });
                await promiseTimeout(tmpSDK.closeBatches({ settings: { batchIds: [executionId], serverUrl, apiKey } }), CLOSE_APPLITOOLS_BATCHES_TIMEOUT);
            } catch {
                // No need to log
            }
        }
    }

    private async runTestPlan(
        beforeTests: ExecutionList,
        tests: ExecutionList,
        afterTests: ExecutionList,
        tpOptions: RunnerOptions,
        testPlanName: string,
        testPlanId: string | null,
        branch: string,
        isAnonymous?: boolean,
    ) {
        const executionId = utils.guid();
        const projectId = tpOptions.project;
        Logger.setExecutionId(executionId);
        beforeTests.forEach(test => Object.assign(test, { isBeforeTestPlan: true }));
        afterTests.forEach(test => Object.assign(test, { isAfterTestPlan: true }));
        const fullTestList = [...beforeTests, ...tests, ...afterTests] as ExecutionList;
        const testStatus = new TestRunStatus(fullTestList, tpOptions, testPlanId, branch);

        const configs = _.chain(fullTestList)
            .map<string>(test => (test as any).overrideTestConfig?.name || '')
            .uniq()
            .compact()
            .value();
        const configName = configs?.length === 1 ? configs[0] : null;

        const testNames = tpOptions.lightweightMode?.onlyTestIdsNoSuite ? [] : fullTestList.map(test => (test as { name: string }).name);

        const testListInfoPromise = tpOptions.lightweightMode?.onlyTestIdsNoSuite ?
            { beforeTests, tests, afterTests } :
            testStatus.executionStart(executionId, projectId, this.startTime, testPlanName, testNames);
        perf.log('before testListInfoPromise');
        const testListInfo = await testListInfoPromise;
        if (!tpOptions.lightweightMode?.onlyTestIdsNoSuite) {
            reporter.onTestPlanStarted(testListInfo.beforeTests, testListInfo.tests, testListInfo.afterTests, testPlanName, executionId, isAnonymous, configName);
        }

        perf.log('before runTestAllPhases');
        const results = await this.runTestAllPhases(testListInfo.beforeTests, testListInfo.tests, testListInfo.afterTests, branch, tpOptions, executionId, testPlanName || 'All Tests', testStatus);

        perf.log('before executionEnd');
        await testStatus.executionEnd(executionId);
        perf.log('after executionEnd');
        return { results, executionId, testPlanName, configName };
    }

    private isJsonString(value: string) {
        try {
            JSON.parse(value);
        } catch {
            return false;
        }
        return true;
    }

    private handleReportFailure(error: any, message: string) {
        try {
            if (error.text) {
                message = error.text;
            } else {
                message = JSON.stringify(error);
            }
        } catch {
            // ignored
        }
        console.error(Color.colorString(Color.FgMagenta, message));
        process.exit(-1);
    }

    private async reportTestRunnerStarted(projectId: string, tpOptions: RunnerOptions) {
        try {
            return await testimServicesApi.reportRunnerStarted(projectId, tpOptions.testOptimization);
        } catch (error) {
            return this.handleReportFailure(error, 'Failed Report: Test Runner Started');
        }
    }

    private async reportTestRunnerFinished(projectId: string) {
        try {
            return await testimServicesApi.reportRunnerFinished(projectId);
        } catch (error) {
            return this.handleReportFailure(error, 'Failed Report: Test Runner Finished');
        }
    }

    private async runTestPlans(options: RunnerOptions, branchToUse: string) {
        logger.info('start to run test plan', {
            options: { ...options, token: undefined, userParamsData: undefined },
            branchToUse,
        });
        type TestPlans = Awaited<ReturnType<typeof testimServicesApi['getTestPlanTestList']>>['testPlans'];
        type TestPlansData = Awaited<ReturnType<typeof testimServicesApi['getTestPlanTestList']>>['testPlansData'];
        type TestPlansDataTests = TestPlansData[string][number]['tests'];
        function flattenTestListData(testPlansData: TestPlansData): any[] {
            return _.flattenDeep(Object.values(testPlansData)).reduce<TestPlansDataTests>((all, testRun) => all.concat(testRun.beforeTests, testRun.tests, testRun.afterTests), []);
        }

        const testPlansResults: { [testPlanId: string]: { [executionId: string]: { [testResultId: string]: any } } } = {};
        const testPlansTests = {};
        const projectId = options.project;
        let data = {
            testPlans: [] as TestPlans,
            testPlansData: {} as TestPlansData,
        };
        if (featureFlagsService.flags.enableTestPlanAggregation.isEnabled()) {
            await utils.promiseMap(options.testPlanIds, async planId => {
                const plan = await testimServicesApi.getTestPlanTestList(projectId, options.testPlan, [planId], branchToUse, options.intersections);
                plan.testPlans.forEach(testPlan => {
                    data.testPlans.push(testPlan);
                    const testPlanId = testPlan._id;
                    data.testPlansData[testPlanId] = plan.testPlansData[testPlanId];
                });
            });
            await utils.promiseMap(options.testPlan, async planName => {
                const plan = await testimServicesApi.getTestPlanTestList(projectId, [planName], options.testPlanIds, branchToUse, options.intersections);
                plan.testPlans.forEach(testPlan => {
                    data.testPlans.push(testPlan);
                    const testPlanId = testPlan._id;
                    data.testPlansData[testPlanId] = plan.testPlansData[testPlanId];
                });
            });
        } else {
            data = await testimServicesApi.getTestPlanTestList(projectId, options.testPlan, options.testPlanIds, branchToUse, options.intersections);

        }
        const testPlans = data.testPlans;
        const testPlansData = data.testPlansData;
        if (!testPlans || testPlans.length === 0) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new ArgError(`no test plan to run ${options.testPlan}`);
        }
        if (!testPlansData || Object.keys(testPlansData).length === 0) {
            if (options.passZeroTests) {
                return [];
            }
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new ArgError(`no test to run in test plan ${options.testPlan}`);
        }
        //TODO: add validation for mobile config
        if (options.mode !== CLI_MODE.APPIUM) {
            await validateConfig(options, flattenTestListData(testPlansData));
        }
        return await utils.promiseMap(testPlans, async testPlan => {
            const id = testPlan.testPlanId;
            testPlansResults[id] = {};

            const testPlanOptions = { ...options };
            testPlanOptions.baseUrl ||= testPlan.startUrl;
            testPlanOptions.appId ||= testPlan.mobileApp?.appId || undefined;
            testPlanOptions.gridId ||= testPlan.gridId;
            //if user pass --grid with test plan we want to use grid option instead of host and port
            if (options.grid) {
                delete testPlanOptions.gridId;
            }

            testPlanOptions.gridData = await gridService.getTestPlanGridData(options, testPlan);

            const testPlanName = testPlanOptions.overrideExecutionName || testPlan.name;
            return await utils.promiseMap(testPlansData[id], async testPlanTests => {
                const res = await this.runTestPlan(testPlanTests.beforeTests, testPlanTests.tests, testPlanTests.afterTests, testPlanOptions, testPlanName, id, branchToUse);
                reporter.onTestPlanFinished(res.results, testPlan.name, this.startTime, res.executionId, false);
                testPlansResults[id][res.executionId] = res.results;
                // TODO: looks like everything below this line, should happen outside of this promiseMap (as it handles the result of all plan-data, but happens on each occurrence)
                const executions = Object.keys(testPlansResults[id]).map(exeId => ({
                    executionId: exeId,
                    status: calcTestResultStatus(testPlansResults[id][exeId]),
                }));
                const tests = Object.keys(testPlansResults[id]).map(exeId => testPlansResults[id][exeId]).reduce((testResult, test) => Object.assign(testResult, test), {});
                const success = calcTestResultStatus(tests);
                Object.assign(testPlansTests, tests);
                const executionId = success ? executions[0].executionId : executions.find(exec => !exec.status)!.executionId;
                await testimServicesApi.saveTestPlanResult(projectId, id, { success, executions, executionId });
                return res;
            });
        });
    }

    private async runAnonymousTestPlan(options: RunnerOptions, branchToUse: string) {
        logger.info('start to run anonymous', {
            options: { ...options, token: undefined },
            branchToUse,
        });

        perf.log('before getSuite');
        const suiteResult = await getSuite(options, branchToUse);
        perf.log('after getSuite');

        if (!suiteResult?.tests[0]?.length) {
            if (options.rerunFailedByRunId) {
                throw new ArgError('No failed tests found in the provided run');
            }
            if (options.passZeroTests) {
                return [];
            }
            throw new ArgError('No tests to run');
        }
        branchToUse = ('branch' in suiteResult && suiteResult.branch) || branchToUse;
        const runListResult = suiteResult as RunListResult;
        if (options.rerunFailedByRunId && !runListResult.runName) {
            if (!runListResult.runExists) {
                throw new ArgError('Invalid run ID - no such run.');
            }
            const isAnonymouslyNamedRun = runListResult.runName === '';
            if (isAnonymouslyNamedRun) {
                runListResult.runName = `rerun-${options.rerunFailedByRunId}`;
            }
        }
        const testPlanName = options.overrideExecutionName || runListResult.runName || ([] as string[]).concat(options.label, options.name, options.suites).join(' ');
        const isAnonymous = true;
        perf.log('Right before validateConfig + runAnonymousTestPlan tests map');

        let nativeAppFromCLIParam: Awaited<ReturnType<typeof testimServicesApi.getAppDetails>>;
        if (options.appId && options.mode === CLI_MODE.APPIUM) {
            nativeAppFromCLIParam = await testimServicesApi.getAppDetails({ appId: options.appId, projectId: options.project });
        }

        return await utils.promiseMap(suiteResult.tests, async suiteTests => { // array of results per execution
            if (nativeAppFromCLIParam) {
                suiteTests.forEach(suiteTest => {
                    if ('nativeApp' in suiteTest) {
                        suiteTest.nativeApp = nativeAppFromCLIParam;
                    }
                });
            }
            // override result id for remote run mode and run only the first test data.
            if (options.resultId) {
                const firstTest = suiteTests[0];
                firstTest.resultId = options.resultId;
                suiteTests = [firstTest];
            }
            //TODO: add validation for mobile config
            if (options.mode !== CLI_MODE.APPIUM) {
                await validateConfig(options, suiteTests);
            }
            perf.log('right before runTestPlan');
            const res = await this.runTestPlan([], suiteTests, [], options, testPlanName, null, branchToUse, isAnonymous);
            perf.log('right after runTestPlan');
            await reporter.onTestPlanFinished(res.results, testPlanName, this.startTime, res.executionId, isAnonymous);
            return res;
        });
    }

    async run(options: RunnerOptions) {
        const branchToUse = branchService.getCurrentBranch();
        let results = [];

        await this.reportTestRunnerStarted(options.project, options);
        if (utils.hasTestPlanFlag(options)) {
            results = await this.runTestPlans(options, branchToUse);
        } else {
            results = await this.runAnonymousTestPlan(options, branchToUse);
        }
        await this.reportTestRunnerFinished(options.project);

        const flattenResults = _.flattenDeep(results);
        perf.log('right before onAllTestPlansFinished');
        await reporter.onAllTestPlansFinished(flattenResults);
        perf.log('right after onAllTestPlansFinished');
        return flattenResults.map(res => res.results).reduce((total, cur) => Object.assign(total, cur), {});
    }
}
